import { type PermissionPath } from "../generated/permissionTypes.js";
import { type EmbeddingSourceType } from "../models/Embedding.js";

export const apiKeyScopes = {
  AssetsRead: "asset:read",
  AssetsWrite: "asset:write",
  TagsRead: "tag:read",
  TagsWrite: "tag:write",
  NotesRead: "note:read",
  NotesWrite: "note:write",
  TasksRead: "task:read",
  TasksWrite: "task:write",
  ContactsRead: "contact:read",
  ContactsWrite: "contact:write",
  FactsRead: "fact:read",
  FactsWrite: "fact:write",
  InsightsRead: "insight:read",
} as const;

export type ApiKeyScope = (typeof apiKeyScopes)[keyof typeof apiKeyScopes];

export type ApiKeyScopeValue = "*" | ApiKeyScope;

export const apiKeyScopeValues = ["*", ...Object.values(apiKeyScopes)] as [
  "*",
  ...ApiKeyScope[],
];

/**
 * Maps each API key scope to the permission an identity's role must hold
 * to grant that scope to a personal API key. `null` means the scope has
 * no corresponding role permission and is grantable by anyone.
 */
export const apiKeyScopePermissions: Record<
  ApiKeyScope,
  PermissionPath | null
> = {
  [apiKeyScopes.AssetsRead]: "asset.read",
  [apiKeyScopes.AssetsWrite]: "asset.update",
  [apiKeyScopes.TagsRead]: null,
  [apiKeyScopes.TagsWrite]: null,
  [apiKeyScopes.NotesRead]: "note.read",
  [apiKeyScopes.NotesWrite]: "note.update",
  [apiKeyScopes.TasksRead]: "task.read",
  [apiKeyScopes.TasksWrite]: "task.update",
  [apiKeyScopes.ContactsRead]: "network.read",
  [apiKeyScopes.ContactsWrite]: "network.update",
  [apiKeyScopes.FactsRead]: "fact.read",
  [apiKeyScopes.FactsWrite]: "fact.update",
  [apiKeyScopes.InsightsRead]: "insight.read",
};

/**
 * Which scope a key needs to read a given embeddable entity type. Written out
 * rather than derived from the `<Group>Read` key names, so adding a source type
 * breaks the build instead of silently resolving to nothing.
 */
export const readScopeBySourceType: Record<EmbeddingSourceType, ApiKeyScope> = {
  fact: apiKeyScopes.FactsRead,
  note: apiKeyScopes.NotesRead,
  asset: apiKeyScopes.AssetsRead,
  contact: apiKeyScopes.ContactsRead,
};

/**
 * "*" means full access, an explicit list means only those, [] means nothing.
 */
export function grantsScope(
  scopes: ApiKeyScopeValue[],
  scope: ApiKeyScope,
): boolean {
  return scopes.includes("*") || scopes.includes(scope);
}

/**
 * Whether the key can reach facts at all. Picking stacks is useless without
 * this, so it also defines if a stack picker is usable.
 */
export function hasFactAccess(scopes: ApiKeyScopeValue[]): boolean {
  return (
    grantsScope(scopes, apiKeyScopes.FactsRead) ||
    grantsScope(scopes, apiKeyScopes.FactsWrite)
  );
}
